import { useEffect, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';
import { createCreditNote } from '../../api/creditNotes';
import { listSites } from '../../api/sites';
import { apiErrorMessage } from '../../lib/api';
import { useToast } from '../../components/ui/Toast';
import { Modal } from '../../components/ui/Modal';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Button } from '../../components/ui/Button';
import { Alert } from '../../components/ui/Alert';

interface LineDraft {
  siteId: string;
  description: string;
  netAmount: string;
}

function emptyLine(defaultSiteId: string): LineDraft {
  return { siteId: defaultSiteId, description: '', netAmount: '' };
}

// §20: manual, not auto-generated - an MSP Admin decides the amount, reason
// and which site(s) it applies to. VAT is left for the backend to compute
// from the org's current rate (same fallback as invoicing) rather than
// asked for here, to keep the form simple for the common case.
export function IssueCreditNoteModal({
  orgId,
  invoiceId,
  open,
  onClose,
}: {
  orgId: string;
  invoiceId?: string;
  open: boolean;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const [reason, setReason] = useState('');
  const [lines, setLines] = useState<LineDraft[]>([emptyLine('')]);
  const [error, setError] = useState<string | null>(null);

  const sitesQuery = useQuery({ queryKey: ['sites', orgId], queryFn: () => listSites(orgId), enabled: open });

  useEffect(() => {
    if (!open) return;
    setReason('');
    setError(null);
    const firstSiteId = sitesQuery.data?.[0]?.id ?? '';
    setLines([emptyLine(firstSiteId)]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, sitesQuery.data]);

  const mutation = useMutation({
    mutationFn: () =>
      createCreditNote(orgId, {
        invoiceId,
        reason: reason.trim(),
        lines: lines.map((l) => ({ siteId: l.siteId, description: l.description.trim(), netAmount: Number(l.netAmount) })),
      }),
    onSuccess: (creditNote) => {
      queryClient.invalidateQueries({ queryKey: ['credit-notes'] });
      showToast(`Credit note ${creditNote.creditNumber} issued.`);
      onClose();
    },
    onError: (err) => setError(apiErrorMessage(err, 'Could not issue the credit note.')),
  });

  function updateLine(index: number, patch: Partial<LineDraft>) {
    setLines((prev) => prev.map((l, i) => (i === index ? { ...l, ...patch } : l)));
  }

  function addLine() {
    setLines((prev) => [...prev, emptyLine(sitesQuery.data?.[0]?.id ?? '')]);
  }

  function removeLine(index: number) {
    setLines((prev) => prev.filter((_, i) => i !== index));
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!reason.trim()) {
      setError('Reason is required.');
      return;
    }
    if (lines.length === 0 || lines.some((l) => !l.siteId || !l.description.trim() || !l.netAmount || Number(l.netAmount) <= 0)) {
      setError('Every line needs a site, description and a net amount greater than zero.');
      return;
    }
    mutation.mutate();
  }

  const total = lines.reduce((sum, l) => sum + (Number(l.netAmount) || 0), 0);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Issue credit note"
      footer={
        <>
          <Button variant="secondary" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button form="issue-credit-note-form" type="submit" isLoading={mutation.isPending}>
            Issue credit note
          </Button>
        </>
      }
    >
      <form id="issue-credit-note-form" onSubmit={handleSubmit} className="flex flex-col gap-4">
        {error && <Alert tone="error">{error}</Alert>}
        {invoiceId && (
          <p className="text-xs text-slate-500">Applied against this invoice.</p>
        )}
        <Input label="Reason" required value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Overcharged for October instalment" />

        <div className="flex flex-col gap-3">
          {lines.map((line, i) => (
            <div key={i} className="flex items-end gap-2 rounded-lg border border-slate-100 p-3">
              <Select
                label="Site"
                className="w-40"
                value={line.siteId}
                onChange={(e) => updateLine(i, { siteId: e.target.value })}
              >
                <option value="">Select a site</option>
                {(sitesQuery.data ?? []).map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </Select>
              <Input
                label="Description"
                className="flex-1"
                value={line.description}
                onChange={(e) => updateLine(i, { description: e.target.value })}
              />
              <Input
                label="Net amount (£)"
                type="number"
                step="0.01"
                min="0.01"
                className="w-32"
                value={line.netAmount}
                onChange={(e) => updateLine(i, { netAmount: e.target.value })}
              />
              {lines.length > 1 && (
                <Button type="button" variant="secondary" size="sm" onClick={() => removeLine(i)} aria-label="Remove line">
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>
          ))}
          <Button type="button" variant="secondary" size="sm" className="self-start" onClick={addLine}>
            <Plus className="h-3.5 w-3.5" /> Add line
          </Button>
        </div>

        <div className="flex justify-end border-t border-slate-100 pt-3 text-sm text-slate-600">
          Net total: <span className="ml-1 font-semibold text-slate-900">£{total.toFixed(2)}</span>
        </div>
      </form>
    </Modal>
  );
}
